import React, { useState } from 'react';
import laboratoryInfo from '../config';
import { useData } from '../contexts/DataContext';
import DocumentPreview from './DocumentPreview';
import { FaFileInvoiceDollar, FaPrint } from 'react-icons/fa';

/**
 * StatementGenerator
 * Generates an Account Statement (Relance / Relevé de Compte)
 */
const StatementGenerator = ({ clientId, clientName, buttonLabel = "Relevé / Relance", className }) => {
    const { invoices, payments, creditNotes, getClientAccountingSummary } = useData();
    const [showPreview, setShowPreview] = useState(false);

    // Filter relevant data
    const clientInvoices = invoices.filter(inv =>
        inv.clientId === clientId || inv.practitionerName === clientName || inv.clientName === clientName
    ).sort((a, b) => new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt));

    const clientPayments = payments.filter(p => p.clientId === clientId)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    const clientCreditNotes = creditNotes.filter(n => n.clientId === clientId)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    const summary = getClientAccountingSummary(clientId, clientName);

    // Combine for the table
    const transactions = [
        ...clientInvoices.map(inv => ({
            date: inv.date || inv.createdAt,
            type: 'Facture',
            ref: inv.invoiceNumber || inv.invoice_number || inv.caseNumber || `#${inv.id.substr(0, 8).toUpperCase()}`,
            debit: parseFloat(inv.amount || inv.total),
            credit: 0
        })),
        ...clientPayments.map(p => {
            const amt = parseFloat(p.amount || 0);
            const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
            return {
                date: p.date,
                type: isRefund ? 'Remboursement' : 'Règlement',
                ref: p.reference || (isRefund ? 'REFUND' : 'RG'),
                debit: isRefund ? amt : 0,
                credit: isRefund ? 0 : amt
            };
        }),
        ...clientCreditNotes.map(n => ({
            date: n.date,
            type: 'Avoir',
            ref: n.reference || 'AV',
            debit: 0,
            credit: parseFloat(n.amount)
        }))
    ].sort((a, b) => new Date(a.date) - new Date(b.date));

    const safeClientName = clientName || 'Client';
    const fileName = `releve-${safeClientName.replace(/\s+/g, '_')}-${new Date().toISOString().split('T')[0]}`;
    const targetId = `statement-img-${clientId || 'unknown'}`;

    return (
        <>
            <button
                className={className || "btn btn-sm btn-outline-secondary"}
                onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
            >
                <FaPrint className="me-1" /> {buttonLabel}
            </button>

            {showPreview && (
                <DocumentPreview
                    targetId={targetId}
                    fileName={fileName}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Hidden Template for Image Capture */}
            <div id={targetId} style={{ display: 'none' }}>
                <div style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px', borderBottom: '2px solid #333', paddingBottom: '20px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 10px 0', color: '#1e3a8a' }}>{laboratoryInfo.name}</h2>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.address}</p>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>Tel: {laboratoryInfo.phone}</p>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.email}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h1 style={{ margin: '0 0 10px 0', color: '#1e3a8a', fontSize: '24px' }}>RELEVÉ DE COMPTE</h1>
                            <p style={{ margin: '2px 0', fontSize: '14px' }}>Date: {new Date().toLocaleDateString()}</p>
                        </div>
                    </div>

                    <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ width: '50%', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                            <h3 style={{ marginTop: '0', fontSize: '14px', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Praticien:</h3>
                            <p style={{ fontWeight: 'bold', margin: '5px 0', fontSize: '16px', color: '#1e293b' }}>Docteur {clientName}</p>
                        </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '40px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#1e3a8a', color: 'white' }}>
                                <th style={{ textAlign: 'left', padding: '10px' }}>Date</th>
                                <th style={{ textAlign: 'left', padding: '10px' }}>Opération</th>
                                <th style={{ textAlign: 'left', padding: '10px' }}>Référence</th>
                                <th style={{ textAlign: 'right', padding: '10px' }}>Débit (+)</th>
                                <th style={{ textAlign: 'right', padding: '10px' }}>Crédit (-)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((t, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                    <td style={{ padding: '10px', fontSize: '13px' }}>{new Date(t.date).toLocaleDateString()}</td>
                                    <td style={{ padding: '10px', fontSize: '13px' }}>{t.type}</td>
                                    <td style={{ padding: '10px', fontSize: '13px', fontStyle: 'italic' }}>{t.ref}</td>
                                    <td style={{ textAlign: 'right', padding: '10px', fontSize: '13px' }}>{t.debit > 0 ? `${t.debit.toFixed(2)} €` : ''}</td>
                                    <td style={{ textAlign: 'right', padding: '10px', fontSize: '13px', color: t.credit > 0 ? '#16a34a' : 'inherit' }}>{t.credit > 0 ? `${t.credit.toFixed(2)} €` : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <div style={{ width: '350px', backgroundColor: '#fdf2f2', padding: '20px', borderRadius: '8px', border: '1px solid #fee2e2' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '14px' }}>
                                <span style={{ color: '#64748b' }}>Total Facturé (Débit):</span>
                                <span style={{ fontWeight: 600 }}>{summary.totalBilled.toFixed(2)} €</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '14px' }}>
                                <span style={{ color: '#64748b' }}>Total Réglé (Crédit):</span>
                                <span style={{ fontWeight: 600, color: '#16a34a' }}>-{(summary.totalPaid + summary.totalCredits).toFixed(2)} €</span>
                            </div>
                            <div style={{ 
                                 display: 'flex', 
                                 justifyContent: 'space-between', 
                                 padding: '12px 0', 
                                 borderTop: summary.balance > 0.01 ? '2px solid #ef4444' : '2px solid #16a34a', 
                                 marginTop: '10px', 
                                 fontSize: '18px', 
                                 fontWeight: 'bold', 
                                 color: summary.balance > 0.01 ? '#b91c1c' : '#16a34a' 
                             }}>
                                 <span>{summary.balance < -0.01 ? 'SOLDE EN VOTRE FAVEUR:' : 'SOLDE DÛ:'}</span>
                                 <span>
                                     {summary.balance < -0.01 
                                         ? `${Math.abs(summary.balance).toFixed(2)} €` 
                                         : `${summary.balance.toFixed(2)} €`}
                                 </span>
                             </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '40px', fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>
                        <p style={{ fontWeight: 'bold' }}>Note:</p>
                        <p>Merci de bien vouloir régulariser votre situation dans les plus brefs délais si votre solde est débiteur.</p>
                        <p>En cas de question ou de contestation, n'hésitez pas à nous contacter.</p>
                    </div>

                    <div style={{
                        marginTop: 'auto',
                        paddingTop: '60px',
                        borderTop: '1px solid #e2e8f0',
                        textAlign: 'center',
                        fontSize: '11px',
                        color: '#64748b',
                    }}>
                        <p style={{ fontWeight: 'bold', margin: '0 0 4px 0' }}>{laboratoryInfo.name} - SIRET: {laboratoryInfo.siret}</p>
                        <p style={{ margin: 0 }}>Document généré le {new Date().toLocaleString()}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StatementGenerator;
